import IconButton from "components/button/IconButton";
import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import ConfirmModal from "components/modal/ConfirmModal";
import { useModal } from "hooks/useModal";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { BiPen, BiTrash, BiUser } from "react-icons/bi";
import { FiRefreshCw } from "react-icons/fi";
import {
  useGetUserProfileQuery,
  useUpdateUserPasswordMutation,
  useUpdateUserProfileMutation,
} from "reduxFiles/api/userApi";
import { useAppDispatch } from "reduxFiles/hooks";
import { useState } from "react";
import { uploadFile } from "services/uploadService";
import { BsPen } from "react-icons/bs";
import { API_URL } from "reduxFiles/api/baseApi";
import { setUserProfile } from "reduxFiles/slices/authSlice";
import {
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import { HiOutlineMail } from "react-icons/hi";
import { RiShieldCheckLine } from "react-icons/ri";

const ProfileOverview = () => {
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isHoveringAvatar, setIsHoveringAvatar] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data, refetch } = useGetUserProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateApi, { isLoading: isUpdating }] = useUpdateUserProfileMutation();
  const [updatePasswordApi, { isLoading: isUpdatingPassword }] =
    useUpdateUserPasswordMutation();

  const modal = useModal();
  const passwordModal = useModal();
  const confirmModal = useModal();

  const handleSubmit = async (
    formData: FieldValues,
    closeModal: () => void
  ) => {
    try {
      const user = await updateApi(formData).unwrap();
      await refetch();
      dispatch(setUserProfile(user.data.avatar));
      closeModal();
    } catch (err) {
      console.error(err);
    }
  };

  const handleChangePassword = async (
    formData: FieldValues,
    closeModal: () => void
  ) => {
    try {
      await updatePasswordApi({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      }).unwrap();
      closeModal();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    setIsUploadingAvatar(true);
    try {
      const uploadedFile: any = await uploadFile(file);
      await updateApi({ avatar: uploadedFile.name }).unwrap();
      await refetch();
      dispatch(setUserProfile(uploadedFile.name));
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async (closeModal: () => void) => {
    try {
      await updateApi({ avatar: null }).unwrap();
      await refetch();
      dispatch(setUserProfile(null));
      closeModal();
    } catch (err) {
      console.error(err);
    }
  };

  const fields: Field[] = [
    {
      name: "fullname",
      label: t("user.fullname"),
      type: "text",
      formed: true,
      required: true,
    },
    {
      name: "email",
      label: t("user.email"),
      type: "email",
      formed: true,
      disabled: true,
    },
    {
      name: "mobile",
      label: t("user.mobile"),
      type: "mobile",
      formed: true,
      required: true,
    },
    { name: "avatar", label: t("user.avatar"), type: "string", hidden: true },
  ];

  const passwordFields = [
    {
      name: "currentPassword",
      label: t("user.currentPassword"),
      type: "password",
      formed: true,
      required: true,
    },
    {
      name: "newPassword",
      label: t("user.newPassword"),
      type: "password",
      formed: true,
      required: true,
    },
  ];

  const getRoleBadge = (role: string) => {
    const roles: Record<string, { color: string; label: string }> = {
      ADMIN: {
        color: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
        label: t("user.roles.ADMIN"),
      },
      USER: {
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
        label: t("user.roles.USER"),
      },
    };
    return (
      roles[role] || {
        color:
          "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
        label: t("user.roles.USER"),
      }
    );
  };

  const getStatusBadge = (status: string) => {
    const statuses: Record<string, { color: string; label: string }> = {
      ACTIVE: {
        color:
          "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400",
        label: t("user.statuses.ACTIVE"),
      },
      INACTIVE: {
        color:
          "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
        label: t("user.statuses.INACTIVE"),
      },
      SUSPENDED: {
        color: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
        label: t("user.statuses.SUSPENDED"),
      },
      PENDING: {
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
        label: t("user.statuses.PENDING"),
      },
    };
    return statuses[status] || statuses.INACTIVE;
  };

  const roleBadge = getRoleBadge(data?.role);
  const statusBadge = getStatusBadge(data?.status);
  const organizationBadge = data?.organization !== null;
  const user = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 lg:p-6"
    >
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-transparent text-2xl font-bold text-gray-900 dark:text-gray-300">
              {t("user.profileUser")}
            </h1>
            <IconButton
              handler={refetch}
              title={t("buttons.refresh")}
              icon={<FiRefreshCw />}
              className="rounded-full bg-white shadow-md dark:bg-gray-800"
            />
          </div>
        </div>

        <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl dark:bg-gray-800/90">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10" />
          <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

          <div className="relative p-6 lg:p-8">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
              {/* Avatar Section */}
              <div
                className="flex flex-col items-center gap-4"
                onMouseEnter={() => setIsHoveringAvatar(true)}
                onMouseLeave={() => setIsHoveringAvatar(false)}
              >
                <div className="relative">
                  <div className="relative h-32 w-32 overflow-hidden rounded-2xl border-4 border-white shadow-lg dark:border-gray-700">
                    {user?.avatar ? (
                      <img
                        src={`${API_URL}/file/${user.avatar}`}
                        alt={user.fullname}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100">
                        <BiUser size={50} className="text-gray-400" />
                      </div>
                    )}
                    <AnimatePresence>
                      {isHoveringAvatar && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="bg-black/50 absolute inset-0 flex items-center justify-center"
                        >
                          <label className="cursor-pointer rounded-full bg-white p-2 transition-colors hover:bg-blue-50">
                            <BiPen size={16} className="text-blue-600" />
                            <input
                              type="file"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (!file.type.startsWith("image/")) return;
                                await handleAvatarUpload(file);
                                e.target.value = "";
                              }}
                              accept="image/jpeg,image/png,image/jpg,image/webp"
                            />
                          </label>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-800" />
                  {user?.avatar && (
                    <button
                      onClick={confirmModal.openModal}
                      className="absolute -bottom-2 -right-2 rounded-full bg-red-500 p-1.5 shadow-lg transition-colors hover:bg-red-600"
                    >
                      <BiTrash size={12} className="text-white" />
                    </button>
                  )}
                </div>
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                    {user?.fullname || t("user.roles.USER")}
                  </h2>
                  <div className="mt-1 flex items-center justify-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${roleBadge.color}`}
                    >
                      <HiOutlineShieldCheck size={12} />
                      {roleBadge.label}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge.color}`}
                    >
                      <HiOutlineShieldCheck size={12} />
                      {statusBadge.label}
                    </span>
                    {organizationBadge && (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-500/20 dark:text-gray-400`}
                      >
                        <HiOutlineShieldCheck size={12} />
                        {t("user.organization")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Info Cards */}
              <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  {
                    icon: <HiOutlineMail className="h-5 w-5" />,
                    bg: "bg-blue-100",
                    textBg: "text-blue-600",
                    label: t("user.email"),
                    value: user?.email,
                    color: "blue",
                  },
                  {
                    icon: <HiOutlinePhone className="h-5 w-5" />,
                    bg: "bg-green-100",
                    textBg: "text-green-600",
                    label: t("user.mobile"),
                    value: user?.mobile,
                    color: "green",
                  },
                  {
                    icon: <HiOutlineUser className="h-5 w-5" />,
                    bg: "bg-purple-100",
                    textBg: "text-purple-600",
                    label: t("user.fullname"),
                    value: user?.fullname,
                    color: "purple",
                  },
                  ...(user?.organization !== null
                    ? [
                        {
                          icon: <RiShieldCheckLine className="h-5 w-5" />,
                          bg: "bg-gray-100",
                          textBg: "text-gray-600",
                          label: t("user.organizationName"),
                          value: user?.organization?.name,
                          color: "gray",
                        },
                      ]
                    : []),
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="group rounded-xl bg-gray-50 p-4 transition-all hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`rounded-lg ${item.bg} p-2 ${item.textBg} dark:bg-${item.color}-500/20`}
                      >
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {item.label}
                        </p>
                        <p className="text-sm font-semibold text-gray-800 dark:text-white">
                          {item.value || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col gap-3 border-t border-gray-200 pt-6 dark:border-gray-700 sm:flex-row sm:justify-center">
              <button
                onClick={passwordModal.openModal}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-600 dark:hover:bg-blue-900/20"
              >
                <BiPen size={16} />
                {t("user.setPassword")}
              </button>
              <button
                onClick={modal.openModal}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:from-blue-600 hover:to-blue-700"
              >
                <BsPen size={16} />
                {t("user.editProfile")}
              </button>
            </div>
          </div>
        </div>

        <DynamicForm
          open={modal.isOpen}
          onClose={modal.closeModal}
          onSubmit={(formData) => handleSubmit(formData, modal.closeModal)}
          fields={fields.filter((f) => f.formed)}
          defaultValues={
            user
              ? {
                  fullname: user.fullname,
                  email: user.email,
                  mobile: user.mobile,
                }
              : {}
          }
          title={t("user.editProfile")}
          isEdit={false}
          loading={isUpdating}
        />
        <DynamicForm
          open={passwordModal.isOpen}
          onClose={passwordModal.closeModal}
          onSubmit={(formData) =>
            handleChangePassword(formData, passwordModal.closeModal)
          }
          fields={passwordFields.filter((f) => f.formed)}
          title={t("user.setPassword")}
          isEdit={false}
          loading={isUpdatingPassword}
        />
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={confirmModal.closeModal}
          handler={() => handleDeleteAvatar(confirmModal.closeModal)}
          loading={isUpdating}
          title={t("user.confirmDeleteAvatarTitle")}
          desc={t("user.confirmDeleteAvatarText")}
        />
      </div>

      <AnimatePresence>
        {isUploadingAvatar && (
          <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
            <div className="rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">
              <div className="flex flex-col items-center gap-3">
                <div className="loader w-6 text-blue-700" />
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t("user.uploadingAvatar")}
                </p>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProfileOverview;
